//
/**
 * @doc-source /cmsApp/shared/DocumentationRenderer/schemas/core-global-javascript-functions.docs.json
 */
import { ButtonLocker } from 'utils/ButtonLocker.js';

window.cmsGlobal = window.cmsGlobal || {};

if (!window.cmsGlobal.ButtonLocker) {
    window.cmsGlobal.ButtonLocker = ButtonLocker;
}

/**
 * Check whether the current session is still authenticated.
 *
 * @returns {Promise<*>} Result of checkLoginStatus().
 */
window.cmsGlobal.checkSession = async function () {
    return await window.cmsGlobal.checkLoginStatus();
};

/**
 * Fetch JSON with CMS defaults and error handling.
 *
 * @param {string} url Request URL.
 * @param {object} [options] Fetch options; set lCheckSession=false to skip session check.
 * @returns {Promise<*>} Parsed JSON (or text) response body.
 * @throws {Error} On HTTP errors or explicit error payloads.
 */
window.cmsGlobal.fetchJson = async function (url, options = {}) {
    const lCheckSession = options?.lCheckSession ?? true;
    if (lCheckSession) {
        if (!(await window.cmsGlobal.checkSession())) return;
    }

    const securityDefaults = {
        'X-Cms-Internal-Token': window.cmsInternalToken || 'unnamed',
        'X-Intent-Token': 'unnamed',
        'X-Intent-Action': 'unnamed'
    };

    const mergedHeaders = {
        ...securityDefaults,
        ...(options?.headers || {})
    };

    const isFormData = options?.body instanceof FormData;

    // Only force JSON content-type if NOT FormData and caller didn't set it.
    if (!isFormData && !('Content-Type' in mergedHeaders)) {
        mergedHeaders['Content-Type'] = 'application/json';
    }

    // If we're sending JSON and body is a plain object, stringify it.
    let body = options?.body;
    if (!isFormData && body && typeof body === 'object' && !(body instanceof Blob) && !(body instanceof ArrayBuffer)) {
        // If caller already gave a string, leave it alone.
        if (!(body instanceof String) && typeof body !== 'string') {
            body = JSON.stringify(body);
        }
    }

    const res = await fetch(url, {
        credentials: 'same-origin',
        cache: 'no-cache',
        ...options,
        body,
        headers: mergedHeaders
    });

    const ct = res.headers.get('content-type') || '';
    const parsed = ct.includes('application/json') ? await res.json().catch(() => null) : await res.text();

    if (!res.ok) {
        const msg = parsed && typeof parsed === 'object' && parsed.error ? parsed.error : typeof parsed === 'string' && parsed.trim() ? parsed : `HTTP ${res.status}`;
        throw new Error(msg);
    }

    return parsed;
};

/**
 * Convert a form's inputs into a plain object payload expected by many endpoints.
 *
 * @param {HTMLFormElement} form Form element to serialize.
 * @returns {{ formData: Record<string, FormDataEntryValue> }} Plain object created from FormData entries.
 * @throws {Error} When the provided form is missing.
 */
window.cmsGlobal.buildFormPayload = function (form) {
    if (!form) throw new Error('Form not found.');
    const formData = new FormData(form);
    return { formData: Object.fromEntries(formData.entries()) };
};

/*----------------------------------------------------------------------
    Radio proxy sync
    - Ensures every radiogroup *_proxy hidden input always matches :checked
    - Runs on load + on any radio change (event delegation)
----------------------------------------------------------------------*/
window.cmsGlobal.syncRadioProxies = function (root = document) {
    const proxyInputs = root.querySelectorAll('input[data-proxy="true"][data-proxy-group="radiogroup"]');

    proxyInputs.forEach((proxy) => {
        const baseKey = proxy.name.replace(/_proxy$/, '');
        const groupName = proxy.dataset.groupName || baseKey;

        const checked = root.querySelector(`input[type="radio"][name="${CSS.escape(baseKey)}"][data-group-name="${CSS.escape(groupName)}"]:checked`) || root.querySelector(`input[type="radio"][name="${CSS.escape(baseKey)}"]:checked`);

        proxy.value = checked ? checked.value : '';
    });
};

/*----------------------------------------------------------------------
    Payload builder (hardened)
    - Still prefers *_proxy
    - BUT if proxy is empty, falls back to the checked radio value
----------------------------------------------------------------------*/
window.cmsGlobal.buildFormPayloadFromProxies = function (form) {
    if (!form) throw new Error('Form not found.');

    // make sure radios are synced right before reading FormData
    window.cmsGlobal.syncRadioProxies(form);

    const formData = new FormData(form);
    const entries = Object.fromEntries(formData.entries());
    const payload = {};

    Object.keys(entries).forEach((key) => {
        if (!key.endsWith('_proxy')) return;

        const baseKey = key.replace('_proxy', '');
        const unitKey = `${baseKey}[unit]`;

        let proxyValue = entries[key] ?? '';
        let finalValue;

        // fallback for radio groups (proxy empty => read :checked)
        if (proxyValue === '') {
            const checked = form.querySelector(`input[type="radio"][name="${CSS.escape(baseKey)}"]:checked`);
            if (checked) proxyValue = checked.value;
        }

        if (entries[unitKey] !== undefined) {
            const unit = entries[unitKey] ?? '';
            const valuePart = proxyValue.includes(',') ? proxyValue.split(',')[0] : proxyValue;
            finalValue = `${valuePart}${unit}`;
        } else {
            finalValue = proxyValue;
        }

        if (finalValue === 'YES') finalValue = true;
        if (finalValue === 'NO') finalValue = false;

        payload[baseKey] = finalValue;
    });

    return { formData: payload };
};

/**
 * Check login status via API; redirects to login on failure.
 *
 * @returns {Promise<boolean|undefined>} True if authenticated; otherwise redirects.
 */
window.cmsGlobal.checkLoginStatus = async function () {
    try {
        const token = window.cmsIntents && window.cmsIntents.Core_intents ? window.cmsIntents.Core_intents : null;

        const res = await window.cmsGlobal.fetchJson(window.cmsRoutes.apiCheckSession, {
            lCheckSession: false,
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Cms-Internal-Token': window.cmsInternalToken,
                'X-Intent-Token': token,
                'X-Intent-Action': 'check-session'
            },
            credentials: 'same-origin',
            cache: 'no-cache',
            body: JSON.stringify({ cms_internal: '1' })
        });
        if (res && res.authenticated === true) return true;
    } catch (e) {
        void e;
        window.location.href = '/cmsApp/module/Login/logout';
    }
};

/**
 * Show an error toast if the shared toast system is available.
 *
 * @param {string} message Message to display.
 */
window.cmsGlobal.toastError = function (message) {
    window.shared_Toast?.error?.(message, {
        title: 'Error',
        duration: 5000,
        dismissible: true
    });
};

/**
 * Show an info toast if the shared toast system is available.
 *
 * @param {string} message Message to display.
 */
window.cmsGlobal.toastInfo = function (message) {
    window.shared_Toast?.info?.(message, {
        title: 'Info',
        duration: 5000,
        dismissible: true
    });
};

/**
 * Generate a random alphanumeric suffix (crypto when available).
 *
 * @param {number} [len=6] Total length.
 * @returns {string} Random suffix.
 */
window.cmsGlobal.randomIdSuffix = function (len = 6) {
    let s = '';
    const c = globalThis.crypto;
    while (s.length < len) {
        const n = c ? c.getRandomValues(new Uint32Array(1))[0] : (Math.random() * 0xffffffff) >>> 0;
        s += n.toString(36);
    }
    return s.slice(0, len);
};

/**
 * Ensure a CMS plugin/namespace is loaded and ready.
 *
 * @param {string} key Plugin key/namespace.
 * @returns {Promise<*>} Resolved plugin instance.
 * @throws {Error} When plugin cannot be run.
 */
window.cmsEnsure = async function (key) {
    const sys = window.__cmsSystem;
    if (!sys) throw new Error('cmsSystem not available');
    const ready = sys.ready(key);
    if (ready) return await ready;
    console.log('Ready', sys);

    if (typeof sys.run === 'function' && key.includes('/')) {
        await sys.run(key);
        return await sys.ready(key);
    }
    throw new Error('Plugin not loaded and cannot be run without namespace');
};

/**
 * Use a CMS plugin; optionally run a callback with its instance.
 *
 * @param {string} key Plugin key/namespace.
 * @param {Function} [fn] Callback receiving the instance.
 * @returns {Promise<*>} Plugin instance or callback result.
 */
window.cmsUse = async function (key, fn) {
    const inst = await window.cmsEnsure(key);
    return typeof fn === 'function' ? fn(inst) : inst;
};

/**
 * Debounce a function with optional immediate call.
 *
 * @param {Function} fn Function to debounce.
 * @param {number} [delay=200] Delay in ms.
 * @param {boolean} [immediate=false] Call on leading edge.
 * @returns {Function} Debounced function.
 */
window.debounce = function (fn, delay = 200, immediate = false) {
    let t;
    return function (...args) {
        const ctx = this;
        const callNow = immediate && !t;
        clearTimeout(t);
        t = setTimeout(() => {
            t = null;
            if (!immediate) fn.apply(ctx, args);
        }, delay);
        if (callNow) fn.apply(ctx, args);
    };
};

/**
 * Get a value by matching the name in an array of objects.
 *
 * @param {Array} list Array of objects with name/value.
 * @param {string} cName Name to search for.
 * @returns {*|null} Matched value or null if not found.
 */
window.getValueByName = function (list, cName) {
    if (!Array.isArray(list)) return null;
    const item = list.find((o) => o && o.name === cName);
    return item ? item.value : null;
};

/**
 * Populate a select element with options from widget settings.
 *
 * @param {HTMLSelectElement} selectEl Target select element.
 * @param {Array|object} data Options data; objects with value/label/sort/active.
 */
window.populateSelectFromWidgetSettings = function (selectEl, data) {
    if (!(selectEl instanceof Element) || selectEl.tagName !== 'SELECT') return;
    const arr = Array.isArray(data) ? data.slice() : [data];
    arr.sort((a, b) => (a?.sort ?? 0) - (b?.sort ?? 0));
    selectEl.innerHTML = '';
    for (const o of arr) {
        if (!o || !o.active) continue;
        const opt = document.createElement('option');
        opt.value = String(o.value ?? '');
        opt.textContent = String(o.label ?? '');
        selectEl.appendChild(opt);
    }
};

/**
 * Return the value of the first selected option in an array.
 *
 * @param {Array} options Options with a selected flag.
 * @returns {*|null} Selected value or null.
 */
window.getSelectedValue = function (options) {
    if (!Array.isArray(options)) return null;
    const selected = options.find((item) => item.selected === true);
    return selected ? selected.value : null;
};

/**
 * Slugify a string to lowercase, dash-separated text.
 *
 * @param {string} text Input text.
 * @returns {string} Slugified string.
 */
window.slugify = function (text) {
    return text
        .toString()
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '-') // Replace non-alphanum with dash
        .replace(/^-+|-+$/g, ''); // Remove leading/trailing dashes
};

/**
 * Make a directory-safe name (lowercase, underscores).
 *
 * @param {string} text Input text.
 * @returns {string} Sanitized directory-safe string.
 */
window.dirSafeName = function (text) {
    return text
        .toString()
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
};

/**
 * Extract the last numeric segment from a URL.
 *
 * @param {string} url Input URL.
 * @returns {number} Last path segment as number (NaN if non-numeric).
 */
window.getLastNumberFromUrl = function (url) {
    const parts = url.split('/').filter(Boolean);
    const last = parts[parts.length - 1];
    return Number(last);
};
